import {
    CoreDI,
    CurrencyFormatter,
    Device, DeviceType,
    LanguageService,
    ObjectBuilder,
    ResolutionManager,
    ResourceLoader, ScreenManager, Scroll, ScreenOrientation, inject
} from '@gongxigames/framework/core';
import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {GlobalEventsMapForBackwardCompatibility} from '@gongxigames/framework/slot/events';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {GameConfig} from '@gongxigames/framework/slot/types';
import {ResourceManager} from '@gongxigames/framework/slot/services/ResourcesManager';
import {TemplateUtils} from '@gongxigames/framework/slot/services/TemplateUtils';
import {strictUiEmitter} from '@gongxigames/framework/slotV2/globals';
import {HtmlSearch} from '@gongxigames/framework/core/fragments/HtmlSearch';

export abstract class InfoModule extends Module<null, GlobalEventsMapForBackwardCompatibility> {
    public static readonly NAME: symbol = Symbol('InfoModule');

    @inject(CoreDI.ResourceLoader)
    protected _loader!: ResourceLoader;

    @inject(DI.ResourceManager)
    protected _resourceManager!: ResourceManager;

    @inject(CoreDI.ObjectBuilder)
    protected _objectBuilder!: ObjectBuilder;

    @inject(CoreDI.CurrencyFormatter)
    protected _currencyFormatter!: CurrencyFormatter;

    @inject(CoreDI.ResolutionManager)
    protected _resolutionManager!: ResolutionManager;

    @inject(DI.TemplateUtils)
    protected _templateUtils!: TemplateUtils;

    @inject(CoreDI.LanguageService)
    protected _languageService!: LanguageService;

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    @inject(CoreDI.Device)
    protected _device!: Device;

    @inject(CoreDI.ScreenManager)
    protected _screenManager!: ScreenManager;

    protected _currentContainer?: HTMLElement;
    protected _currentPage?: HTMLElement;
    protected _currentScroll?: Scroll;

    protected _isPortrait = false;

    protected abstract htmlSearch: HtmlSearch;

    private readonly offsetTop = {
        [DeviceType.MOBILE]: {
            [ScreenOrientation.PORTRAIT]: 728,
            [ScreenOrientation.LANDSCAPE]: 390
        },
        [DeviceType.DESKTOP]: {
            [ScreenOrientation.PORTRAIT]: 0,
            [ScreenOrientation.LANDSCAPE]: 0
        }
    } as const;

    protected setupViews(): void {
    }

    protected setupStates(): void {
    }

    protected setupEvents(): void {
        this.on('General.Screen.RESIZED', this.onResized);

        strictUiEmitter.on('UI.menu.menuBarClosed', this.clearHtmlSearchInput.bind(this));

        if (!this.isPlatformDesktop()) {
            this.on('General.Screen.ORIENTATION_CHANGED', this.onOrientationChanged);
        }
    }

    private clearHtmlSearchInput(): void {
        this.htmlSearch?.clear();
    }

    protected open(): void {
        this.setCurrentElements();

        this._currentContainer!.classList.add('open');

        this.onResized();

        // TODO: Find good solution. It fixes bug on ios devices
        window.scrollBy(0, window.scrollY === 1 ? 0 : 1);

        this.eventManager.emit('General.UI.DISABLE', InfoModule.NAME);
        this.htmlSearch?.searchSet();
        this.isPlatform(DeviceType.MOBILE) && this.htmlSearch?.restorePrevQuery();
    }

    protected onOrientationChanged(listenerId: string, orientation: ScreenOrientation): void {
        if (orientation === ScreenOrientation.PORTRAIT) {
            this._isPortrait = true;
            document.body.classList.add('portrait');
        } else {
            this._isPortrait = false;
            document.body.classList.remove('portrait');
        }
    }

    protected close(): void {
        this._currentContainer && this._currentContainer.classList.remove('open');

        this.eventManager.emit('General.UI.ENABLE', InfoModule.NAME);
        this.htmlSearch?.searchSet();
        this.htmlSearch?.savePrevQuery();
        this.isPlatform(DeviceType.DESKTOP) && this.clearHtmlSearchInput();
    }

    protected onResized(): void {
        this.fitToGameDimensions();
        this.setContentSize();
    }

    protected setContentSize(): void {
        const pixelRatio = this._device.getPixelRatio();
        const screenParams = this._screenManager.getScreenState();
        const {x, scale, orientation, y, width, height} = screenParams;
        const offsetTop = this.getOffsetTop(this._settings.get('platform'), orientation) * scale;

        const resW = width * scale / pixelRatio;
        const resH = (height * scale - offsetTop + (y * 2)) / pixelRatio;
        const resY = offsetTop / pixelRatio;
        const resX = x / pixelRatio;

        this.setContentSizeProps(resX, resY, resW, resH);
    }

    protected fitToGameDimensions(): void {
        if (this.isPlatformDesktop() && this._currentPage) {
            const state = this._screenManager.getScreenState();
            const pixelRatio = this._device.getPixelRatio();
            const scale = state.width * state.scale / pixelRatio / this._gameConfig.viewport.desktop.width;

            this._currentPage.style.height = this._currentPage.getBoundingClientRect().width * 0.55 + 'px';
            this._currentPage.style.fontSize = 100 * scale + 'px';

            this._currentScroll && this._currentScroll.onResize();
        }
    }

    public abstract setCurrentElements(): void;

    private setContentSizeProps(x: number, y: number, width: number, height: number): void {
        const content = this._currentPage?.querySelector('.content') as any;
        const searchRoot = this._currentPage?.querySelector('.search-root') as HTMLDivElement;
        let searchHeight = 0;

        if (!content) {
            return;
        }

        if (searchRoot) {
            searchRoot.style.top = `${y}px`;
            searchHeight = searchRoot.getBoundingClientRect().height;
        }

        content.style.top = `${y + searchHeight}px`;
        content.style.left = x + 'px';
        content.style.width = width + 'px';
        content.style.height = `${height - searchHeight}px`;

        content.scrollTo(0, 0);
        // hack for CLFR-2308
        requestAnimationFrame(() => content.scrollTo(0, 0));
    }

    private getOffsetTop(device: DeviceType, orientation: ScreenOrientation): number {
        return this.offsetTop[device][orientation];
    }
}
